import React from 'react';
import { Link } from 'react-router-dom';

import { api } from '../services/api';
import type { AgentProfile, AgentTrip } from '../services/types';

import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';

export default function AgentProfilePage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [profile, setProfile] = React.useState<AgentProfile | null>(null);
  const [trips, setTrips] = React.useState<AgentTrip[]>([]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [profileRes, tripsRes] = await Promise.all([api.getAgentProfile(), api.getAgentTrips()]);
        if (!mounted) return;
        setProfile(profileRes);
        setTrips(tripsRes);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load agent profile');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="page heroPage">
      <div className="container">
        <PageHeader
          kicker="Agent profile"
          title={profile ? profile.name : 'Loading...'}
          subtitle="Specializations, transparency, and upcoming group trips."
        />

        {loading && <div className="muted">Loading profile...</div>}
        {!loading && error && <div className="badge badgeWarn">{error}</div>}

        {!loading && !error && profile && (
          <div className="grid cols2" style={{ alignItems: 'start' }}>
            <Card title="About">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {profile.heroImageUrl && (
                  <img className="mediaImage mediaImageTall" src={profile.heroImageUrl} alt="Safari landscape" />
                )}
                <div>
                  <div className="muted">Location</div>
                  <div style={{ fontWeight: 900, marginTop: 4 }}>{profile.location}</div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span className="badge">{profile.yearsOfExperience} years</span>
                  {profile.languages.map((l) => (
                    <span key={l} className="badge badgeAccent">
                      {l}
                    </span>
                  ))}
                </div>

                <div>
                  <div className="label">Specializations</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {profile.specializations.map((s) => (
                      <span key={s} className="badge">{s}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="label">Bio</div>
                  <div className="muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
                    {profile.bio}
                  </div>
                </div>

                <div>
                  <div className="label">Philosophy</div>
                  <div className="muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
                    {profile.philosophy}
                  </div>
                </div>

                <div>
                  <div className="label">Certifications</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {profile.certifications.map((c) => (
                      <span key={c} className="badge badgeAccent">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Upcoming group trips">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {trips.length === 0 ? (
                  <div className="muted">No upcoming trips.</div>
                ) : (
                  trips.map((t) => (
                    <div key={t.id} className="card" style={{ boxShadow: 'none', borderStyle: 'dashed' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <img className="mediaImage" src={t.imageUrl} alt={t.title} />
                        <div style={{ fontWeight: 900 }}>{t.title}</div>
                        <div className="muted" style={{ lineHeight: 1.5 }}>
                          {t.destination}
                          <br />
                          <span className="muted">{t.dates}</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                          <span className="badge">Group size: {t.groupSize}</span>
                          <span className="badge badgeAccent">Seats left: {t.remainingSeats}</span>
                        </div>

                        <Link className="btn btnPrimary" to={`/booking/${t.packageId}`}>
                          View &amp; Book
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

